import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import { Title, useGetList, useNotify, usePermissions } from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import { hasPermission } from "@/utils/permission-utils";
import { apiErrorMessage, todayLocalYmd } from "../shared/api-error";
import { downloadTextFile } from "../shared/csv";
import { formatRupees } from "../shared/fee-format";
import { MADRASA_FEES_VIEW } from "../shared/madrasa-permissions";
import { defaultersToCsv, type DefaulterRow } from "./report-utils";

type YearRow = { id: string; name: string; isCurrent: boolean };
type ClassRow = { id: string; name: string };
type Report = { asOf: string; count: number; totalOverdue: number; rows: DefaulterRow[] };
const ALL = "ALL";

/** Students with fees overdue as of a date, worst first, with the guardian's contact. */
export default function DefaultersPage() {
  const { permissions } = usePermissions();
  const notify = useNotify();
  const years = useGetList<YearRow>("academicYears", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "startDate", order: "DESC" },
  });
  const classes = useGetList<ClassRow>("madrasaClasses", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "sortOrder", order: "ASC" },
  });
  const [yearId, setYearId] = useState("");
  const [classId, setClassId] = useState(ALL);
  const [asOf, setAsOf] = useState(todayLocalYmd());
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!yearId && years.data?.length)
      setYearId((years.data.find((y) => y.isCurrent) ?? years.data[0]).id);
  }, [years.data, yearId]);

  useEffect(() => {
    if (!yearId || !asOf) return;
    setLoading(true);
    callApi({
      location: "feeReports/defaulters",
      method: "GET",
      data: { academicYearId: yearId, asOf, ...(classId !== ALL ? { classId } : {}), limit: 5000 },
    })
      .then((r) => setReport(r.data as Report))
      .catch((error: unknown) => {
        setReport(null);
        notify(apiErrorMessage(error, "Could not load the report"), { type: "error" });
      })
      .finally(() => setLoading(false));
  }, [yearId, classId, asOf, notify]);

  if (!hasPermission(permissions, MADRASA_FEES_VIEW)) return null;

  return (
    <>
      <Title title="Fee defaulters" />
      <Box sx={{ p: 2, maxWidth: 1100 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Fee defaulters
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Students who still owe fees that fell due before the chosen date.
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 2, alignItems: { sm: "center" } }}
        >
          <TextField
            select
            label="Academic year"
            value={yearId}
            onChange={(e) => setYearId(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            {(years.data ?? []).map((y) => (
              <MenuItem key={y.id} value={y.id}>
                {y.name}
                {y.isCurrent ? " (current)" : ""}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Class"
            value={classId}
            onChange={(e) => setClassId(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value={ALL}>All classes</MenuItem>
            {(classes.data ?? []).map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Overdue as of"
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            helperText="Fees due before this date count."
          />
          <Button
            variant="outlined"
            disabled={!report || report.rows.length === 0}
            onClick={() =>
              report &&
              downloadTextFile(
                `fee-defaulters-${report.asOf}.csv`,
                defaultersToCsv(report.rows, report.asOf)
              )
            }
          >
            Export CSV
          </Button>
        </Stack>

        {loading && <CircularProgress size={28} />}
        {report && !loading && (
          <>
            <Alert severity={report.count ? "warning" : "success"} sx={{ mb: 2 }}>
              {report.count
                ? `${report.count} ${report.count === 1 ? "student owes" : "students owe"} ${formatRupees(report.totalOverdue)} overdue as of ${report.asOf}.`
                : `Nobody has overdue fees as of ${report.asOf}.`}
            </Alert>
            {report.rows.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "60vh" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Adm. no</TableCell>
                      <TableCell>Student</TableCell>
                      <TableCell>Class</TableCell>
                      <TableCell>Guardian</TableCell>
                      <TableCell align="right">Overdue</TableCell>
                      <TableCell align="right">Dues</TableCell>
                      <TableCell>Oldest</TableCell>
                      <TableCell align="right">Total outstanding</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.rows.map((r) => (
                      <TableRow key={r.studentId} hover>
                        <TableCell>{r.admissionNo}</TableCell>
                        <TableCell>
                          <Link to={`/feeLedger?studentId=${r.studentId}`}>{r.name}</Link>
                        </TableCell>
                        <TableCell>{r.className}</TableCell>
                        <TableCell>
                          {r.guardianName ?? "—"}
                          {r.guardianPhone && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {r.guardianPhone}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatRupees(r.overdueAmount)}</strong>
                        </TableCell>
                        <TableCell align="right">{r.overdueCount}</TableCell>
                        <TableCell>{r.oldestDueDate ?? "—"}</TableCell>
                        <TableCell align="right">{formatRupees(r.totalOutstanding)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Box>
    </>
  );
}
